import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  Link as MuiLink,
  Avatar,
  CircularProgress,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  KeyboardArrowDown,
  OpenInNew,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import {
  KadryEmployee,
  EmployeeKind,
  getEmployee,
  updateEmployee,
  listEmployees,
  listEmployeeLeaveRequests,
  listEmployeeDocuments,
  createEmployeeDocument,
  deleteEmployeeDocument,
  uploadEmployeePhoto,
  deleteEmployeePhoto,
  KadryLeaveRequest,
  KadryDocument,
  KadryEmployeeUpdatePayload
} from '../services/kadryService';

// -----------------------------------------------------------------------------
// Style globalne (spójne z KadryPage.tsx / ProjectDetailPage.tsx)
// -----------------------------------------------------------------------------

const PRIMARY_BLUE = '#4040DB';
const PRIMARY_BLUE_HOVER = '#1F1FB0';
const PRIMARY_BLUE_ACTIVE = '#1A1A98';
const HEADER_TEXT = '#020242';
const TEXT_DARK = '#2D2D32';
const TEXT_MUTED = '#595966';
const BORDER_LIGHT = '#d4d4d9';
const BG_SOFT = '#FAFAFA';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    const iso = d.length === 10 ? d + 'T12:00:00Z' : d;
    return new Intl.DateTimeFormat('pl-PL').format(new Date(iso));
  } catch {
    return String(d);
  }
}

function contractTypeLabel(ct: string | null | undefined): string {
  if (!ct) return '—';
  const m: Record<string, string> = { uop: 'UoP', b2b: 'B2B', uz: 'Umowa zlecenie' };
  return m[String(ct).toLowerCase()] || String(ct);
}

function contractPeriodText(fixed: boolean | null | undefined, end: string | null | undefined): string {
  if (fixed === true) return end ? `Do ${fmtDate(end)}` : 'Określony — brak daty końca';
  if (fixed === false) return 'Nieokreślony';
  return '—';
}

function daysUntil(d: string | null | undefined): number | null {
  if (!d) return null;
  try {
    const iso = d.length === 10 ? d + 'T12:00:00Z' : d;
    const diff = Math.floor((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diff;
  } catch {
    return null;
  }
}

function medicalStatus(valid_until: string | null | undefined): { label: string; color: 'success' | 'warning' | 'error' | 'default' } {
  if (!valid_until) return { label: 'brak danych', color: 'default' };
  const du = daysUntil(valid_until);
  if (du == null) return { label: 'brak danych', color: 'default' };
  if (du < 0) return { label: 'nieaktualne', color: 'error' };
  if (du <= 30) return { label: `za ${du} dni`, color: 'warning' };
  return { label: 'aktualne', color: 'success' };
}

function initialsFromName(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// -----------------------------------------------------------------------------
// Resolver kind+id z samego id
// -----------------------------------------------------------------------------

async function resolveEmployee(rawId: string, kindHint?: EmployeeKind | null): Promise<KadryEmployee | null> {
  const tryKinds: EmployeeKind[] = kindHint ? [kindHint] : ['user', 'external'];
  for (const k of tryKinds) {
    try {
      const emp = await getEmployee(k, rawId);
      if (emp) return emp;
    } catch {
      // spróbuj kolejny typ
    }
  }
  // Fallback – skan listy (np. gdy id nie pasuje jeden-do-jeden)
  try {
    const list = await listEmployees();
    const found = list.find((e) => String(e.id) === String(rawId) || (e.user_id && String(e.user_id) === String(rawId)));
    return found || null;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Strona szczegółów pracownika
// -----------------------------------------------------------------------------

const KadryEmployeePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [employee, setEmployee] = React.useState<KadryEmployee | null>(null);
  const [form, setForm] = React.useState<KadryEmployee | null>(null);
  const [leaves, setLeaves] = React.useState<KadryLeaveRequest[]>([]);
  const [docs, setDocs] = React.useState<KadryDocument[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [headerTab, setHeaderTab] = React.useState(0);
  const [docForm, setDocForm] = React.useState<{ title: string; url: string; doc_type: string; notes: string }>({ title: '', url: '', doc_type: '', notes: '' });
  const [photoUploading, setPhotoUploading] = React.useState(false);
  const photoInputRef = React.useRef<HTMLInputElement | null>(null);

  const headerTabs = ['Dane', 'Urlopy', 'Zaliczki', 'Dokumenty'];

  const kindHint = (searchParams.get('kind') as EmployeeKind | null) || null;

  const load = React.useCallback(async () => {
    if (!id) { setError('Brak identyfikatora pracownika'); setLoading(false); return; }
    try {
      setLoading(true);
      setError(null);
      const emp = await resolveEmployee(id, kindHint);
      if (!emp) { setError('Nie znaleziono pracownika'); setEmployee(null); setForm(null); return; }
      setEmployee(emp);
      setForm({ ...emp });
      const [lv, dc] = await Promise.all([
        listEmployeeLeaveRequests(emp.kind, emp.id).catch(() => []),
        listEmployeeDocuments(emp.kind, emp.id).catch(() => [])
      ]);
      setLeaves(lv);
      setDocs(dc);
    } catch (e: any) {
      setError(e?.message || 'Błąd podczas pobierania pracownika');
    } finally {
      setLoading(false);
    }
  }, [id, kindHint]);

  React.useEffect(() => { load(); }, [load]);

  // Skrót klawiaturowy: 'u' – powrót do listy kadr
  React.useEffect(() => {
    const isEditable = (el: EventTarget | null): boolean => {
      const e = el as HTMLElement | null;
      if (!e) return false;
      const tag = (e.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || (e as any).isContentEditable) return true;
      let p: HTMLElement | null = e.parentElement;
      while (p) {
        const t = (p.tagName || '').toLowerCase();
        if (t === 'input' || t === 'textarea' || (p as any).isContentEditable) return true;
        p = p.parentElement;
      }
      return false;
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'u' && ev.code !== 'KeyU') return;
      if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) return;
      if (isEditable(ev.target)) return;
      ev.preventDefault();
      navigate('/app/kadry');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const set = (patch: Partial<KadryEmployee>) => setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleSave = async () => {
    if (!form || !employee) return;
    try {
      setSaving(true);
      setSaveMsg(null);
      const payload: KadryEmployeeUpdatePayload = {
        leave_days_entitlement: form.leave_days_entitlement,
        leave_days_used: form.leave_days_used,
        medical_exam_last_date: form.medical_exam_last_date,
        medical_exam_valid_until: form.medical_exam_valid_until,
        hire_date: form.hire_date,
        termination_date: form.termination_date,
        kadry_notes: form.kadry_notes,
        position: form.position,
        contract_type: form.contract_type ?? null,
        contract_is_fixed_term: form.contract_is_fixed_term ?? null,
        contract_end_date: form.contract_end_date ?? null,
        bhp_general_valid_until: form.bhp_general_valid_until ?? null,
        bhp_position_valid_until: form.bhp_position_valid_until ?? null,
        kadry_work_mode: form.kadry_work_mode ?? null
      };
      if (employee.kind === 'user') {
        payload.telefon = form.phone;
      } else {
        payload.first_name = form.first_name || undefined;
        payload.last_name = form.last_name || undefined;
        payload.display_name = form.display_name;
        payload.email = form.email;
        payload.phone = form.phone;
        payload.pesel = (form as any).pesel ?? null;
        payload.is_active = form.is_active;
        payload.notes = form.kadry_notes;
      }
      const updated = await updateEmployee(employee.kind, employee.id, payload);
      setEmployee(updated);
      setForm({ ...updated });
      setSaveMsg('Zapisano zmiany.');
      setTimeout(() => setSaveMsg(null), 2500);
    } catch (e: any) {
      console.error('[KadryEmployeePage] update failed', e);
      window.alert('Nie udało się zapisać: ' + (e?.response?.data?.message || e?.message || 'błąd'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddDoc = async () => {
    if (!employee) return;
    if (!docForm.title.trim() || !docForm.url.trim()) {
      window.alert('Podaj tytuł i URL dokumentu.');
      return;
    }
    try {
      const newDoc = await createEmployeeDocument(employee.kind, employee.id, {
        title: docForm.title.trim(),
        url: docForm.url.trim(),
        doc_type: docForm.doc_type.trim() || null,
        notes: docForm.notes.trim() || null
      });
      setDocs((prev) => [newDoc, ...prev]);
      setDocForm({ title: '', url: '', doc_type: '', notes: '' });
    } catch (e: any) {
      window.alert('Nie udało się dodać dokumentu: ' + (e?.response?.data?.message || e?.message || 'błąd'));
    }
  };

  const handlePickPhoto = () => {
    try { photoInputRef.current?.click(); } catch {}
  };

  const handlePhotoFileChange = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const f = ev.target.files && ev.target.files[0];
    // Zresetuj input od razu, żeby dało się wybrać ten sam plik ponownie.
    try { ev.target.value = ''; } catch {}
    if (!f || !employee) return;
    if (!/^image\//.test(f.type)) {
      window.alert('Wybierz plik graficzny (JPG/PNG/WEBP/GIF).');
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      window.alert('Plik jest zbyt duży (max 8 MB).');
      return;
    }
    try {
      setPhotoUploading(true);
      const updated = await uploadEmployeePhoto(employee.kind, employee.id, f);
      setEmployee(updated);
      setForm((prev) => (prev ? { ...prev, avatar_url: updated.avatar_url } : prev));
    } catch (e: any) {
      console.error('[KadryEmployeePage] uploadPhoto failed', e);
      window.alert('Nie udało się wgrać zdjęcia: ' + (e?.response?.data?.message || e?.message || 'błąd'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!employee || !form?.avatar_url) return;
    const ok = window.confirm('Usunąć zdjęcie pracownika?');
    if (!ok) return;
    try {
      setPhotoUploading(true);
      const updated = await deleteEmployeePhoto(employee.kind, employee.id);
      setEmployee(updated);
      setForm((prev) => (prev ? { ...prev, avatar_url: updated.avatar_url } : prev));
    } catch (e: any) {
      console.error('[KadryEmployeePage] deletePhoto failed', e);
      window.alert('Nie udało się usunąć zdjęcia: ' + (e?.response?.data?.message || e?.message || 'błąd'));
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleDeleteDoc = async (doc: KadryDocument) => {
    const ok = window.confirm(`Usunąć dokument: ${doc.title}?`);
    if (!ok) return;
    try {
      await deleteEmployeeDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch {}
  };

  const advanceRequests = React.useMemo(() => leaves.filter((l) => /zalicz/i.test(l.title || '')), [leaves]);
  const vacationRequests = React.useMemo(() => leaves.filter((l) => !/zalicz/i.test(l.title || '')), [leaves]);

  const kindChip = (kind: EmployeeKind) => (
    <Chip
      label={kind === 'user' ? 'Użytkownik aplikacji' : 'Pracownik fizyczny'}
      size="small"
      sx={{
        bgcolor: kind === 'user' ? '#E6F0FF' : '#F1F1F6',
        color: HEADER_TEXT,
        borderRadius: '4px',
        minHeight: 28,
        height: 28,
        '& .MuiChip-label': {
          px: 1,
          py: 0,
          fontFamily: 'Roboto, sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '16px',
          color: HEADER_TEXT
        }
      }}
    />
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
        <CircularProgress size={24} />
        <Typography>Ładowanie pracownika…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <IconButton size="small" onClick={() => navigate('/app/kadry')} aria-label="Wstecz" sx={{ mb: 1 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!employee || !form) {
    return (
      <Box sx={{ p: 2 }}>
        <IconButton size="small" onClick={() => navigate('/app/kadry')} aria-label="Wstecz" sx={{ mb: 1 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography>Pracownik nie został znaleziony.</Typography>
      </Box>
    );
  }

  const med = medicalStatus(form.medical_exam_valid_until);

  return (
    <Box sx={{ bgcolor: BG_SOFT, mt: '20px', position: 'relative', pb: 4 }}>
      {/* Wróć do kadr – nad kontenerem, w stylu ProjectDetailPage */}
      <Box
        onClick={() => navigate('/app/kadry')}
        sx={{
          position: 'absolute',
          top: '-24px',
          left: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        aria-label="Wróć do kadr"
        role="button"
      >
        <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: PRIMARY_BLUE_HOVER }} />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            color: PRIMARY_BLUE_HOVER
          }}
        >
          Wróć do kadr
        </Typography>
      </Box>

      {/* Nagłówek */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0, mb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar src={form.avatar_url || undefined} sx={{ width: 40, height: 40, bgcolor: form.kind === 'user' ? PRIMARY_BLUE : TEXT_MUTED, fontSize: 14 }}>
            {initialsFromName(form.full_name)}
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '20px',
                  lineHeight: '24px',
                  fontWeight: 400,
                  color: HEADER_TEXT,
                  m: 0
                }}
              >
                {form.full_name}
              </Typography>
              {kindChip(form.kind)}
              {!form.is_active && (
                <Chip
                  label="Nieaktywny"
                  size="small"
                  sx={{
                    bgcolor: '#FEECEC',
                    color: '#B91C1C',
                    borderRadius: '4px',
                    minHeight: 28,
                    height: 28,
                    '& .MuiChip-label': { px: 1, py: 0, fontSize: 14, color: '#B91C1C' }
                  }}
                />
              )}
            </Box>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, color: TEXT_MUTED, mt: '2px' }}>
              {form.position || 'Brak stanowiska'}
              {form.phone ? ` · ${form.phone}` : ''}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: '24px' }}>
            {form.google_drive_user_folder_link && (
              <Tooltip title="Folder pracownika na Google Drive">
                <IconButton
                  size="small"
                  component="a"
                  href={form.google_drive_user_folder_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ p: 0.25, width: 32, height: 32 }}
                  aria-label="Dysk Google"
                >
                  <Box component="img" src="/assets/icons/drive.svg" alt="Dysk" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              width: 140,
              height: 40,
              px: 1,
              whiteSpace: 'nowrap',
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: PRIMARY_BLUE,
              color: '#fff',
              '&:hover': { backgroundColor: PRIMARY_BLUE_HOVER },
              '&:active': { backgroundColor: PRIMARY_BLUE_ACTIVE }
            }}
          >
            {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
          </Button>
        </Box>
      </Box>

      {saveMsg && (
        <Alert severity="success" sx={{ mt: 2, py: 0.5 }}>
          {saveMsg}
        </Alert>
      )}

      {/* Pasek zakładek pod nagłówkiem – styl 1:1 z ProjectDetailPage */}
      <Box
        data-tab-scope="main"
        sx={{ mt: '28px', mb: 2, borderBottom: '1px solid #EDEDED', display: 'flex', gap: 3, alignItems: 'flex-end' }}
      >
        {headerTabs.map((label, idx) => {
          const count =
            idx === 1 ? vacationRequests.length
              : idx === 2 ? advanceRequests.length
                : idx === 3 ? docs.length
                  : null;
          return (
            <Box
              key={label}
              data-tab="main"
              aria-selected={headerTab === idx ? 'true' : 'false'}
              onClick={() => setHeaderTab(idx)}
              sx={{
                position: 'relative',
                pb: 1,
                cursor: 'pointer',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                color: HEADER_TEXT,
                fontWeight: headerTab === idx ? 500 : 400,
                userSelect: 'none'
              }}
            >
              {count != null ? `${label} (${count})` : label}
              {headerTab === idx && (
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2, bgcolor: '#000000' }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Zawartość zakładek */}
      {headerTab === 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Ukryty input do wybierania pliku zdjęcia */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handlePhotoFileChange}
            style={{ display: 'none' }}
          />

          {/* Kafelek „zdjęcie + podstawowe informacje” – układ 1:1 z kafelkiem projektu na /projects/:id */}
          <Box
            sx={{
              gridColumn: { md: '1 / span 2' },
              display: 'flex',
              border: '1px solid #EDEDED',
              borderRadius: '4px',
              overflow: 'hidden',
              minHeight: 208,
              bgcolor: '#fff'
            }}
          >
            {/* Zdjęcie (Rectangle 8 – analogicznie do ProjectDetails) */}
            <Box sx={{ position: 'relative', width: 291, flexShrink: 0, backgroundColor: '#D9D9D9' }}>
              {form.avatar_url ? (
                <Box
                  component="img"
                  src={form.avatar_url}
                  alt="Zdjęcie pracownika"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <Box
                  onClick={handlePickPhoto}
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#CFCFCF' }
                  }}
                  role="button"
                  aria-label="Wgraj zdjęcie pracownika"
                >
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>
                    Zdjęcie pracownika
                  </Typography>
                </Box>
              )}

              {/* Przycisk zmiany zdjęcia – identyczny układ jak w ProjectDetails (prawy dolny róg) */}
              <IconButton
                onClick={handlePickPhoto}
                disabled={photoUploading}
                aria-label="Zmień zdjęcie"
                sx={{
                  position: 'absolute',
                  right: 12,
                  bottom: 12,
                  p: 0.75,
                  m: 0,
                  width: 'auto',
                  height: 'auto',
                  bgcolor: 'rgba(255,255,255,0.7)',
                  borderRadius: '999px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  backdropFilter: 'blur(2px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                }}
              >
                <Box
                  component="img"
                  src="/assets/edit_image.png"
                  alt="Edytuj"
                  sx={{ display: 'block', width: 'auto', height: 'auto' }}
                />
              </IconButton>

              {/* Opcjonalnie – ikonka usunięcia zdjęcia (widoczna tylko gdy zdjęcie istnieje) */}
              {form.avatar_url && (
                <IconButton
                  onClick={handleDeletePhoto}
                  disabled={photoUploading}
                  aria-label="Usuń zdjęcie"
                  sx={{
                    position: 'absolute',
                    right: 56,
                    bottom: 12,
                    p: 0.75,
                    width: 32,
                    height: 32,
                    bgcolor: 'rgba(255,255,255,0.7)',
                    borderRadius: '999px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(2px)',
                    color: '#B91C1C',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}

              {photoUploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(255,255,255,0.55)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
            </Box>

            {/* Prawy panel treści – wiersze klucz/wartość (Frame 423 z ProjectDetails) */}
            <Box sx={{ flex: 1, backgroundColor: '#fff', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: '24px', pt: '24px', pb: 0 }}>
                <Box sx={{ display: 'grid', rowGap: 0 }}>
                  {[
                    { label: 'Imię i nazwisko', value: form.full_name || '-' },
                    { label: 'Stanowisko', value: form.position || '-' },
                    { label: 'Email', value: form.email || '-' },
                    { label: 'Telefon', value: form.phone || '-' },
                    {
                      label: form.kind === 'external' ? 'PESEL' : 'Rola',
                      value: form.kind === 'external'
                        ? ((form as any).pesel || '-')
                        : (form.role || '-')
                    },
                    { label: 'Data zatrudnienia', value: fmtDate(form.hire_date) },
                    { label: 'Typ umowy', value: contractTypeLabel(form.contract_type) },
                    { label: 'Okres umowy', value: contractPeriodText(form.contract_is_fixed_term, form.contract_end_date) },
                    {
                      label: 'Miejsce pracy (lista)',
                      value: form.kadry_work_mode === 'field' ? 'Teren i produkcja' : form.kadry_work_mode === 'office' ? 'Praca biurowa' : '—'
                    }
                  ].map((row, idx) => (
                    <Box
                      key={row.label}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 32,
                        backgroundColor: idx % 2 === 1 ? '#F9F9F9' : '#FFFFFF',
                        px: 0
                      }}
                    >
                      <Box sx={{ width: 130, pr: 2 }}>
                        <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#595966' }}>
                          {row.label}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '14px',
                            color: '#020242',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={String(row.value)}
                        >
                          {row.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Karta: Dane osobowe */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 2 }}>
              Dane osobowe
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {form.kind === 'external' && (
                <>
                  <TextField size="small" label="Imię" value={form.first_name || ''} onChange={(e) => set({ first_name: e.target.value })} fullWidth />
                  <TextField size="small" label="Nazwisko" value={form.last_name || ''} onChange={(e) => set({ last_name: e.target.value })} fullWidth />
                </>
              )}
              <TextField
                size="small"
                label="Stanowisko"
                value={form.position || ''}
                onChange={(e) => set({ position: e.target.value })}
                fullWidth
                sx={{ gridColumn: { sm: '1 / span 2' } }}
              />
              <TextField
                size="small"
                label="Email"
                value={form.email || ''}
                onChange={(e) => set({ email: e.target.value })}
                fullWidth
                disabled={form.kind === 'user'}
              />
              <TextField size="small" label="Telefon" value={form.phone || ''} onChange={(e) => set({ phone: e.target.value })} fullWidth />
              {form.kind === 'external' && (
                <TextField
                  size="small"
                  label="PESEL"
                  value={(form as any).pesel || ''}
                  onChange={(e) => set({ ...(form as any), pesel: e.target.value } as any)}
                  fullWidth
                  sx={{ gridColumn: { sm: '1 / span 2' } }}
                />
              )}
            </Box>
          </Paper>

          {/* Karta: Zatrudnienie */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 2 }}>
              Zatrudnienie
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                size="small"
                label="Data zatrudnienia"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.hire_date?.slice(0, 10) || ''}
                onChange={(e) => set({ hire_date: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Data zwolnienia"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.termination_date?.slice(0, 10) || ''}
                onChange={(e) => set({ termination_date: e.target.value })}
                fullWidth
              />
              {form.google_drive_user_folder_link && (
                <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                  <MuiLink href={form.google_drive_user_folder_link} target="_blank" rel="noreferrer">
                    Otwórz folder pracownika na Google Drive <OpenInNew fontSize="inherit" />
                  </MuiLink>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Karta: Umowa i BHP */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff', gridColumn: { md: '1 / span 2' } }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 1 }}>
              Umowa i szkolenia BHP
            </Typography>
            <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: 13 }}>
              Szkolenie <strong>ogólne BHP</strong> jest wymagane przed dopuszczeniem każdego pracownika do pracy. Szkolenie{' '}
              <strong>stanowiskowe BHP</strong> uzupełnia wiedzę o ryzykach i środkach ochrony na danym stanowisku (Kodeks pracy). Częstotliwość odświeżania ustala pracodawca w oparciu o przepisy i ocenę ryzyka.
            </Alert>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Typ umowy</InputLabel>
                <Select
                  label="Typ umowy"
                  value={form.contract_type || ''}
                  onChange={(e: SelectChangeEvent) => set({ contract_type: e.target.value || null })}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="uop">UoP</MenuItem>
                  <MenuItem value="b2b">B2B</MenuItem>
                  <MenuItem value="uz">Umowa zlecenie</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Charakter umowy</InputLabel>
                <Select
                  label="Charakter umowy"
                  value={form.contract_is_fixed_term === true ? 'fixed' : form.contract_is_fixed_term === false ? 'indefinite' : ''}
                  onChange={(e: SelectChangeEvent) => {
                    const v = e.target.value;
                    if (v === 'fixed') set({ contract_is_fixed_term: true });
                    else if (v === 'indefinite') set({ contract_is_fixed_term: false, contract_end_date: null });
                    else set({ contract_is_fixed_term: null, contract_end_date: null });
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="fixed">Na czas określony</MenuItem>
                  <MenuItem value="indefinite">Na czas nieokreślony</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Data zakończenia umowy (czas określony)"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.contract_end_date?.slice(0, 10) || ''}
                onChange={(e) => set({ contract_end_date: e.target.value || null })}
                fullWidth
                disabled={form.contract_is_fixed_term !== true}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Miejsce pracy (zakładki na liście)</InputLabel>
                <Select
                  label="Miejsce pracy (zakładki na liście)"
                  value={form.kadry_work_mode || ''}
                  onChange={(e: SelectChangeEvent) => set({ kadry_work_mode: e.target.value || null })}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="field">Teren i produkcja</MenuItem>
                  <MenuItem value="office">Praca biurowa</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="BHP ogólne — ważne do"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.bhp_general_valid_until?.slice(0, 10) || ''}
                onChange={(e) => set({ bhp_general_valid_until: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="BHP stanowiskowe — ważne do"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.bhp_position_valid_until?.slice(0, 10) || ''}
                onChange={(e) => set({ bhp_position_valid_until: e.target.value })}
                fullWidth
              />
            </Box>
          </Paper>

          {/* Karta: Urlopy */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 2 }}>
              Urlopy
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                size="small"
                label="Przysługujący urlop (dni)"
                type="number"
                value={form.leave_days_entitlement ?? ''}
                onChange={(e) => set({ leave_days_entitlement: e.target.value === '' ? null : Number(e.target.value) })}
                fullWidth
              />
              <TextField
                size="small"
                label="Bilans ręczny — wykorzystane (dni)"
                type="number"
                value={form.leave_days_used ?? ''}
                onChange={(e) => set({ leave_days_used: e.target.value === '' ? null : Number(e.target.value) })}
                fullWidth
                helperText={
                  form.leave_annual_approved_from_requests != null
                    ? `+ ${form.leave_annual_approved_from_requests} dni z wniosków (bieżący rok, tylko po akceptacji). Łącznie: ${form.leave_days_used_total != null ? form.leave_days_used_total : '—'}.`
                    : 'Do sumy doliczane są zatwierdzone wnioski (urlop wypoczynkowy / na żądanie).'
                }
              />
              <TextField
                size="small"
                label="Pozostało (wyliczane)"
                value={
                  form.leave_days_entitlement != null && form.leave_days_used != null
                    ? Math.round(
                        (Number(form.leave_days_entitlement) -
                          Number(form.leave_days_used) -
                          (form.leave_annual_approved_from_requests != null ? Number(form.leave_annual_approved_from_requests) : 0)) *
                          10
                      ) / 10
                    : '—'
                }
                fullWidth
                disabled
                sx={{ gridColumn: { sm: '1 / span 2' } }}
              />
            </Box>
          </Paper>

          {/* Karta: Badania lekarskie */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff' }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 2 }}>
              Badania lekarskie
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                size="small"
                label="Ostatnie badania"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.medical_exam_last_date?.slice(0, 10) || ''}
                onChange={(e) => set({ medical_exam_last_date: e.target.value })}
                fullWidth
              />
              <TextField
                size="small"
                label="Ważne do"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.medical_exam_valid_until?.slice(0, 10) || ''}
                onChange={(e) => set({ medical_exam_valid_until: e.target.value })}
                fullWidth
              />
              <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
                <Chip size="small" label={med.label} color={med.color as any} variant="outlined" />
              </Box>
            </Box>
          </Paper>

          {/* Karta: Notatki – szeroka */}
          <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff', gridColumn: { md: '1 / span 2' } }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 2 }}>
              Notatki kadrowe
            </Typography>
            <TextField
              size="small"
              value={form.kadry_notes || ''}
              onChange={(e) => set({ kadry_notes: e.target.value })}
              multiline
              minRows={4}
              fullWidth
            />
          </Paper>
        </Box>
      )}

      {headerTab === 1 && (
        <LeaveListCard
          title="Urlopy"
          rows={vacationRequests}
          kind={employee.kind}
        />
      )}

      {headerTab === 2 && (
        <LeaveListCard
          title="Zaliczki"
          rows={advanceRequests}
          kind={employee.kind}
        />
      )}

      {headerTab === 3 && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 2 }}>
            Dodaj link do dokumentu
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 3 }}>
            <TextField size="small" label="Tytuł *" value={docForm.title} onChange={(e) => setDocForm((f) => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField size="small" label="Typ (np. umowa, bhp, badania)" value={docForm.doc_type} onChange={(e) => setDocForm((f) => ({ ...f, doc_type: e.target.value }))} fullWidth />
            <TextField size="small" label="URL *" value={docForm.url} onChange={(e) => setDocForm((f) => ({ ...f, url: e.target.value }))} fullWidth sx={{ gridColumn: { sm: '1 / span 2' } }} />
            <TextField size="small" label="Notatki" value={docForm.notes} onChange={(e) => setDocForm((f) => ({ ...f, notes: e.target.value }))} fullWidth sx={{ gridColumn: { sm: '1 / span 2' } }} />
            <Box sx={{ gridColumn: { sm: '1 / span 2' } }}>
              <Button
                variant="contained"
                onClick={handleAddDoc}
                startIcon={<AddIcon />}
                sx={{ textTransform: 'none', backgroundColor: PRIMARY_BLUE, '&:hover': { backgroundColor: PRIMARY_BLUE_HOVER } }}
              >
                Dodaj dokument
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 1 }}>
            Lista dokumentów
          </Typography>
          {docs.length === 0 ? (
            <Typography variant="body2" sx={{ color: TEXT_MUTED }}>Brak dokumentów</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${BORDER_LIGHT}` }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tytuł</TableCell>
                    <TableCell sx={{ width: 140 }}>Typ</TableCell>
                    <TableCell sx={{ width: 160 }}>Dodano</TableCell>
                    <TableCell align="right" sx={{ width: 80 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>
                        <MuiLink href={d.url} target="_blank" rel="noreferrer">
                          {d.title} <OpenInNew fontSize="inherit" />
                        </MuiLink>
                        {d.notes && (<Typography variant="caption" sx={{ display: 'block', color: TEXT_MUTED }}>{d.notes}</Typography>)}
                      </TableCell>
                      <TableCell>{d.doc_type || '—'}</TableCell>
                      <TableCell>{fmtDate(d.created_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Usuń">
                          <IconButton size="small" onClick={() => handleDeleteDoc(d)} sx={{ color: '#B91C1C' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};

// -----------------------------------------------------------------------------
// Lista urlopów / zaliczek (osobna karta)
// -----------------------------------------------------------------------------

const LeaveListCard: React.FC<{ title: string; rows: KadryLeaveRequest[]; kind: EmployeeKind }> = ({ title, rows, kind }) => {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: BORDER_LIGHT, borderRadius: '4px', bgcolor: '#fff' }}>
      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 16, fontWeight: 500, color: HEADER_TEXT, mb: 2 }}>
        {title}
      </Typography>
      {kind === 'external' ? (
        <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
          Pracownicy fizyczni bez konta w aplikacji nie składają wniosków przez aplikację. Rejestruj urlopy ręcznie w zakładce „Dane".
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: TEXT_MUTED }}>Brak wniosków typu „{title}".</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${BORDER_LIGHT}` }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nr</TableCell>
                <TableCell>Data wniosku</TableCell>
                <TableCell>Okres</TableCell>
                <TableCell>Dni</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.request_id}</TableCell>
                  <TableCell>{fmtDate(r.request_date)}</TableCell>
                  <TableCell>{fmtDate(r.date_from)} → {fmtDate(r.date_to)}</TableCell>
                  <TableCell>{r.days}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.status}
                      color={r.status === 'zgoda' ? 'success' : r.status === 'odrzucony' ? 'error' : 'info'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {r.generated_pdf_url ? (
                      <MuiLink href={r.generated_pdf_url} target="_blank" rel="noreferrer">PDF <OpenInNew fontSize="inherit" /></MuiLink>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default KadryEmployeePage;
